import {
  ComponentRef,
  ElementRef,
  EmbeddedViewRef,
  Injectable,
  InjectionToken,
  Injector,
  OnDestroy,
  ViewContainerRef,
  NgZone,
  ApplicationRef,
  PLATFORM_ID,
  Inject,
} from '@angular/core';
import {
  Observable,
  ReplaySubject,
  Subject,
  BehaviorSubject,
  Subscription,
} from 'rxjs';
import { Location } from 'src/app/modules/route-planner/interfaces/route-planning/location.interface';
import { environment } from 'src/environments/environment';
import onResize from 'simple-element-resize-detector';
import { LogService } from 'src/app/@shared/services/log/log.service';
import { ResizeService } from 'src/app/@shared/services/resize/resize.service';
// NOTE: no import of '@here/maps-api-for-javascript' here, runtime comes from CDN 3.2
import { MAP_ICONS } from '../../constants/map-icons.constants';
import { CHOOSE_VIEW } from '../../constants/multi-language.constants';
import { LanguageService } from 'src/app/@shared/services/language/language.service';
import { UserLocationService } from 'src/app/@shared/services/user-location/user-location.service';
import { MyLocationControl } from './my-location.control';
import { ComponentType } from '@angular/cdk/portal';
import html2canvas from 'html2canvas';
import { filter, switchMap, take } from 'rxjs/operators';
import { isPlatformBrowser } from '@angular/common';
// NOTE: Using global H from CDN 3.2 scripts loaded in index.html
// Do NOT import from '@here/maps-api-for-javascript' to avoid version conflicts
declare const H: any;
import borders from './borders.json';

export const MAP_OBJECT_TOKEN = new InjectionToken<string>('MAP_OBJECT_TOKEN');
export const TILE_CACHE_SIZE = 10000; // Maximum number of tiles to cache
export const MIN_ZOOM = 7; // Minimum zoom level (zoom out limit)
export const MAX_ZOOM = 20; // Maximum zoom level (zoom in limit)

// Israel geographical bounds with some buffer
export const MAP_BOUNDS = {
  north: 33.5,  // Northern border
  south: 29.3,  // Southern border (including Eilat)
  east: 36.0,   // Eastern border
  west: 34.0    // Western border (Mediterranean)
};

export type MarkerTrackingOptions = {
  $location: Observable<Location>;
  maxDistance: number;
};

export type MarkersClustering = {
  layers: {
    label?: string;
    dataPoints: MapLocation<any>[];
    dataPointObjectFactory?: (dataPoint: any) => any;
    options?: any;
    tracking?: MarkerTrackingOptions;
  }[];
};

export type MapLocation<T = any> = {
  coordinates: Location;
  data?: T;
};

export type MapOptions = {
  focusOnUserLocation?: boolean;
  markersClustering?: MarkersClustering;
  controls?: {
    [controlName in
      | 'zoom'
      | 'myLocation'
      | 'scalebar'
      | 'mapSettings']?: boolean;
  };
  progressiveLoading?: boolean;
  enableCaching?: boolean;
  politicalView?: string; // optional political view (e.g. 'ISR')
  minZoom?: number;
  maxZoom?: number;
};

interface HarpSyntheticEvent extends H.util.Event {
  originalEvent?: any;
  currentPointer?: any;
}

// Simple LRU-style tile cache (kept for future optimizations)
class TileCache {
  private cache: Map<string, any> = new Map();
  private keys: string[] = [];
  private maxSize: number;

  constructor(maxSize: number) {
    this.maxSize = maxSize;
  }

  get(key: string): any {
    return this.cache.get(key);
  }

  set(key: string, value: any): void {
    if (this.keys.length >= this.maxSize && !this.cache.has(key)) {
      const oldestKey = this.keys.shift();
      if (oldestKey !== undefined) {
        this.cache.delete(oldestKey);
      }
    }

    if (!this.cache.has(key)) {
      this.keys.push(key);
    }

    this.cache.set(key, value);
  }

  has(key: string): boolean {
    return this.cache.has(key);
  }

  clear(): void {
    this.cache.clear();
    this.keys = [];
  }
}

@Injectable({
  providedIn: 'root',
})
export class MapService implements OnDestroy {
  private _map?: any;
  private _mapBehavior?: any;
  private _mapEvents?: any;
  private _ui?: any;
  private _uiCtrls: string[] = [];
  private _layers: any[] = [];

  private _platform?: any;
  private _defaultLayers?: any;

  private _tileCache: TileCache;
  private _isBrowser: boolean;
  private _progressiveLoading: boolean = false;
  private _currentDetailLevel: 'low' | 'high' = 'low';
  private _isPersistentMapInitialized: boolean = false;
  private _currentMapDiv?: ElementRef;
  private _instanceId: string = 'map_' + Date.now();
  private _initCount: number = 0;

  private _mapObjects: any[] = [];
  private _myLocationMarker: any | undefined;
  /** Single chain: permission check → $userLocationChanged (avoid stacking subs on each trackUserLocation). */
  private _trackUserLocationSub?: Subscription;
  private _viewContainerRef!: ViewContainerRef;
  private _isDragging: boolean = false;
  private _hasMoved: boolean = false;
  private _dragStartHandler?: (e: MouseEvent) => void;
  private _dragEndHandler?: (e: MouseEvent) => void;
  private _dragMoveHandler?: (e: MouseEvent) => void;

  public $mapInitialized = new ReplaySubject<any>();
  public $mapViewChanged: Subject<Location> = new Subject<Location>();
  public $mapPreloaded = new BehaviorSubject<boolean>(true); // start as true
  public $isPersistentMapReady = new BehaviorSubject<boolean>(false);

  private readonly DETAIL_LEVEL_THRESHOLD = 12;

  zoom: number = 5;
  lat: number = 0;
  lng: number = 0;

  constructor(
    private readonly languageService: LanguageService,
    private readonly userLocationService: UserLocationService,
    private readonly injector: Injector,
    private readonly ngZone: NgZone,
    private readonly appRef: ApplicationRef,
    private readonly resizeService: ResizeService,
    @Inject(PLATFORM_ID) platformId: Object
  ) {
    const serviceInstanceId = 'mapservice_' + Date.now();
    (this as any).__serviceInstanceId = serviceInstanceId;

    LogService.logInfo('MapService instance created');
    this._isBrowser = isPlatformBrowser(platformId);
    this._tileCache = new TileCache(TILE_CACHE_SIZE);

    this.patchHereStyle();

    if (this._isBrowser) {
      this.initPlatform();
    }
  }

  // ------------- BASIC GETTERS / SETTERS -------------

  public get map(): any {
    return this._map as any;
  }

  private set map(value: any | undefined) {
    if (value) {
      (value as any).__instanceId = this._instanceId;
      this._map = value;
      this.$mapInitialized.next(this._map);
      this.$mapInitialized.complete();
    } else {
      this._map = undefined;
    }
  }

  public get mapObjects(): any[] {
    return this._mapObjects;
  }

  public get mapBehavior(): any | undefined {
    return this._mapBehavior;
  }

  public get mapEvents(): any | undefined {
    return this._mapEvents;
  }

  // ------------- LIFECYCLE -------------

  ngOnDestroy(): void {
    this._trackUserLocationSub?.unsubscribe();
    this._trackUserLocationSub = undefined;

    // Clean up drag prevention handlers
    this.removeDragPrevention();
    
    if (this._map) {
      this._map.dispose();
      LogService.logInfo('Map instance disposed');
    }
  }

  // ------------- PLATFORM + LAYERS INIT (HARP, CDN 3.2) -------------

  private initPlatform(opt?: MapOptions): void {
    if (this._platform && this._defaultLayers) return;

    // Check if HERE Maps API is loaded
    if (typeof H === 'undefined' || !H.service || !H.service.Platform) {
      const error = 'HERE Maps API (H) is not loaded. Make sure CDN scripts are loaded in index.html';
      LogService.logError(error);
      console.error('[MAP]', error);
      throw new Error(error);
    }

    const langSymbol =
      this.languageService?.getSelectedLanguage()?.symbol || 'en';

    // Platform options
    const platformOptions: any = {  
      apikey: environment.here_map_api_key,
    };

    if (opt?.politicalView) {
      // Political view is passed to platform if needed
      platformOptions.politicalView = opt.politicalView;
    }

    this._platform = new H.service.Platform(platformOptions);

    // Default layers for HARP + OMV
    const layerOptions: any = {
      lg: langSymbol,
      engineType: H.Map.EngineType.HARP,
      tileSize: 512,
      politicalView: 'ISR',
      ppi: window.devicePixelRatio > 1 ? 320 : 72,
      //HIDE HERE POIS
      pois: false,
    };

    if (opt?.politicalView) {
      // Some setups accept politicalView here as well; cast as any to bypass TS
      layerOptions.politicalView = opt.politicalView;
    }

    this._defaultLayers = this._platform.createDefaultLayers(
      layerOptions as any
    );
  }

  // ------------- PUBLIC API -------------

  public setViewContainerRef(vcr: ViewContainerRef): void {
    this._viewContainerRef = vcr;
  }

  public createMapObject<T, U = any>(
    componentType: ComponentType<T>,
    data?: U
  ): HTMLElement {
    if (!this._viewContainerRef) {
      throw new Error(
        'ViewContainerRef is undefined in MapService. Make sure to call setViewContainerRef() before using createMapObject.'
      );
    }

    const injector = Injector.create({
      providers: [{ provide: MAP_OBJECT_TOKEN, useValue: data }],
      parent: this.injector,
    });

    const componentRef: ComponentRef<T> =
      this._viewContainerRef.createComponent(componentType, {
        injector,
      });

    const hostView = componentRef.hostView as EmbeddedViewRef<any>;
    const rootNodes = hostView.rootNodes;
    const container = document.createElement('ng-container');

    container.className = 'map-component';
    container.style.pointerEvents = 'all';

    rootNodes.forEach((node) => {
      if (node instanceof HTMLElement) {
        container.appendChild(node);
      }
    });

    componentRef.changeDetectorRef.detectChanges();
    return container;
  }

  // Main entry point for map initialization
  public initializeMap(mapDiv: ElementRef, opt?: MapOptions): boolean {
    this._initCount++;

    if (!this._isBrowser) {
      console.warn('[MAP] Not running in browser, map will not be initialized');
      return false;
    }

    try {
      const options: MapOptions = {
        progressiveLoading: true,
        politicalView: 'ISR',
        enableCaching: true,
        ...opt,
      };

      this._progressiveLoading = !!options.progressiveLoading;
      this._currentMapDiv = mapDiv;

      // Reuse persistent map instance if possible
      if (this._map && this._isPersistentMapInitialized) {
        this.clearMap();
        this.updateMapOptions(options);
        this.enableHarpEvents(this._map);

        return true;
      }

      // Initialize platform + default layers with options
      this.initPlatform(options);


      // Create a new map instance
      return this.doInitializeMap(mapDiv, options);
    } catch (ex) {
      LogService.logError('Failed to initialize map', ex);
      console.error('[MAP_TRACKING] Failed to initialize map', ex);
      return false;
    }
  }

  // ------------- INTERNAL MAP INITIALIZATION -------------

  private doInitializeMap(mapDiv: ElementRef, opt?: MapOptions): boolean {
    this.map = undefined;

    try {
      // Check if HERE Maps API is loaded
      if (typeof H === 'undefined' || !H.Map || !H.Map.EngineType) {
        const error = 'HERE Maps API (H) is not fully loaded. Make sure all CDN scripts are loaded in index.html';
        LogService.logError(error);
        console.error('[MAP]', error);
        throw new Error(error);
      }

      // Ensure platform + layers exist
      this.initPlatform(opt);
      const layers = this._defaultLayers;

      this.ngZone.runOutsideAngular(() => {
        // HARP-compatible vector layer from createDefaultLayers
        const baseLayer = layers.vector.normal.map;

        // Create map with HARP engine
        const map = new H.Map(mapDiv.nativeElement, baseLayer, {
          pixelRatio: window.devicePixelRatio,
          center: environment.map_center,
          zoom: 10,
          engineType: H.Map.EngineType.HARP,
        });
        map.getBaseLayer().setMin(7);
        map.getBaseLayer().setMax(20);
        //@ts-ignore
        map.getBaseLayer().getProvider().setPoliticalView("ISR"); 

        const israelBounds = new H.geo.Rect(
          33.33, 34.15,  
          29.45, 35.90  
        );
        map.getViewModel().addEventListener('sync', () => {
          const vm = map.getViewModel();
          const data = vm.getLookAtData();

          if (!israelBounds.containsLatLng(data.position.lat, data.position.lng)) {
            vm.setLookAtData({
              position: israelBounds.getCenter(),
              zoom: data.zoom
            });
          }
        });

        // Map events + behavior
        this._mapEvents = new H.mapevents.MapEvents(map);
        this._mapBehavior = new H.mapevents.Behavior(this._mapEvents);

        // Prevent text selection during map dragging
        this.setupDragPrevention(mapDiv.nativeElement);

        // Default UI based on default layers
        this._ui = H.ui.UI.createDefault(map, layers);
        this.resizeMapControlButtons();

        // Viewport resize observer
        onResize(mapDiv.nativeElement, () => {
          map.getViewPort().resize();
        });

        // Zoom-dependent detail level + view change emission
        if (this._progressiveLoading) {
          this.applyDetailLevel(map);
          map.addEventListener('mapviewchangeend', () => {
            this.applyDetailLevel(map);
            const location = map.getViewModel().getLookAtData().position;
            this.ngZone.run(() => {
              this.$mapViewChanged.next({
                lat: location.lat,
                lng: location.lng,
              });
            });
          });
        } else {
          map.addEventListener('mapviewchangeend', () => {
            const location = map.getViewModel().getLookAtData().position;
            this.ngZone.run(() => {
              this.$mapViewChanged.next({
                lat: location.lat,
                lng: location.lng,
              });
            });
          });
        }

        this.map = map;
        
        setTimeout(() => {
          try {
            this.hideDisputedBorders();
            this.drawBordersFromGeoJSON(borders);
          } catch (e) {
            console.error('[BORDERS] Failed to apply custom borders', e);
          }
        }, 300);

        // Fired when map view is fully ready and tiles are loaded
        map.addEventListener('mapviewready', () => {
          LogService.logInfo('Map view is ready and all tiles are loaded');
        });

        // Warm up visible tiles
        this.preRenderVisibleTiles(map);
      });

      // Clustering setup
      if (opt?.markersClustering) {
        if (!opt.markersClustering.layers.some((layer) => layer.options)) {
          this.enhanceClusteringOptions(opt.markersClustering);
        }
      }

      this.addMarkersClustring(opt?.markersClustering);
      this.trackUserLocation(opt?.focusOnUserLocation || false);
      this.initializeUi(opt || {});

      this._isPersistentMapInitialized = true;
      this.enableHarpEvents(this._map);
      this.$isPersistentMapReady.next(true);

      return true;
    } catch (ex) {
      LogService.logError('Failed to initialize map in doInitializeMap', ex);
      console.error(
        '[MAP_TRACKING] Failed to initialize map in doInitializeMap',
        ex
      );
      return false;
    }
  }

  // ------------- DRAG PREVENTION (PREVENT TEXT SELECTION) -------------

  private setupDragPrevention(mapElement: HTMLElement): void {
    if (!this._isBrowser) return;

    // Remove existing handlers if any
    this.removeDragPrevention();

    // Handler for mouse down on map
    this._dragStartHandler = (e: MouseEvent) => {
      // Only prevent if clicking on the map itself (not on interactive elements)
      const target = e.target as HTMLElement;
      if (target && mapElement.contains(target)) {
        // Check if clicking on interactive elements (buttons, inputs, etc.)
        const isInteractive = target.closest('button, input, select, textarea, a, [role="button"]');
        if (!isInteractive) {
          this._isDragging = true;
          this._hasMoved = false;
        }
      }
    };

    // Handler for mouse move - prevent text selection while dragging
    this._dragMoveHandler = (e: MouseEvent) => {
      if (this._isDragging) {
        // Only prevent selection if mouse has actually moved (dragging, not just clicking)
        if (!this._hasMoved) {
          // Check if mouse has moved significantly (more than a few pixels)
          const moveThreshold = 3;
          if (Math.abs(e.movementX) > moveThreshold || Math.abs(e.movementY) > moveThreshold) {
            this._hasMoved = true;
            this.preventTextSelection();
          }
        } else {
          // Continue preventing selection while dragging
          e.preventDefault();
          this.preventTextSelection();
        }
      }
    };

    // Handler for mouse up - restore text selection
    this._dragEndHandler = () => {
      if (this._isDragging) {
        this._isDragging = false;
        this._hasMoved = false;
        this.restoreTextSelection();
      }
    };

    // Add event listeners
    mapElement.addEventListener('mousedown', this._dragStartHandler);
    document.addEventListener('mousemove', this._dragMoveHandler);
    document.addEventListener('mouseup', this._dragEndHandler);
    // Also handle mouse leave to ensure cleanup
    document.addEventListener('mouseleave', this._dragEndHandler);
  }

  private removeDragPrevention(): void {
    if (!this._isBrowser) return;

    if (this._currentMapDiv?.nativeElement && this._dragStartHandler) {
      this._currentMapDiv.nativeElement.removeEventListener('mousedown', this._dragStartHandler);
    }
    if (this._dragMoveHandler) {
      document.removeEventListener('mousemove', this._dragMoveHandler);
    }
    if (this._dragEndHandler) {
      document.removeEventListener('mouseup', this._dragEndHandler);
      document.removeEventListener('mouseleave', this._dragEndHandler);
    }

    // Reset state
    this._isDragging = false;
    this._hasMoved = false;
    this.restoreTextSelection();
  }

  private preventTextSelection(): void {
    if (!this._isBrowser) return;
    const bodyStyle = document.body.style;
    bodyStyle.userSelect = 'none';
    bodyStyle.setProperty('-webkit-user-select', 'none');
    bodyStyle.setProperty('-ms-user-select', 'none');
    bodyStyle.setProperty('-moz-user-select', 'none');
    // Also prevent dragging of images
    bodyStyle.setProperty('-webkit-user-drag', 'none');
    bodyStyle.setProperty('user-drag', 'none');
  }

  private restoreTextSelection(): void {
    if (!this._isBrowser) return;
    const bodyStyle = document.body.style;
    bodyStyle.userSelect = '';
    bodyStyle.removeProperty('-webkit-user-select');
    bodyStyle.removeProperty('-ms-user-select');
    bodyStyle.removeProperty('-moz-user-select');
    bodyStyle.removeProperty('-webkit-user-drag');
    bodyStyle.removeProperty('user-drag');
  }

  // ------------- SUPPORT FEATURES (DETAIL LEVEL, UI, CLUSTERING) -------------

  private applyDetailLevel(map: any): void {
    if (!this._progressiveLoading) return;

    const currentZoom = map.getZoom();
    const newDetailLevel =
      currentZoom >= this.DETAIL_LEVEL_THRESHOLD ? 'high' : 'low';

    if (this._currentDetailLevel !== newDetailLevel) {
      this._currentDetailLevel = newDetailLevel;

      this._mapObjects.forEach((obj) => {
        const data = obj.getData && obj.getData();
        if (data && data.alwaysVisible === true) {
          obj.setVisibility(true);
          return;
        }

        if (data && data.detailLevel === 'high') {
          obj.setVisibility(newDetailLevel === 'high');
        }
      });
    }
  }

  private updateMapOptions(opt: MapOptions): void {
    try {
      if (!this._map) return;

      if (opt.markersClustering) {
        this.addMarkersClustring(opt.markersClustering);
      }

      if (opt.focusOnUserLocation !== undefined) {
        this.trackUserLocation(opt.focusOnUserLocation);
      } else if (opt.controls?.myLocation) {
        // Reuse path: clearMap() drops the marker ref — reattach stream without requiring focusOnUserLocation
        this.trackUserLocation(false);
      }

      if (opt.controls) {
        this.initializeUi(opt);
      }
    } catch (error) {
      LogService.logError('Error updating map options', error);
    }
  }

  private initializeUi(options: MapOptions) {
    if (!this._ui || !options.controls) return;

    // Check if HERE Maps UI API is loaded
    if (typeof H === 'undefined' || !H.ui || !H.ui.Control) {
      const error = 'HERE Maps UI API (H.ui) is not loaded. Make sure mapsjs-ui.js is loaded in index.html';
      LogService.logError(error);
      console.error('[MAP]', error);
      return;
    }

    if (options.controls.myLocation) {
      this._ui.addControl(
        'myLocation',
        new MyLocationControl(this, this.userLocationService)
      );
      this._uiCtrls.push('myLocation');
    }

    if (options.controls.mapSettings) {
      const mapSettingsCtrl = new H.ui.MapSettingsControl({
        layers: this._layers,
      });
      this._ui.addControl('customMapSettings', mapSettingsCtrl);
      const langSymbol = this.languageService?.getSelectedLanguage()?.symbol || 'en';
      const langKey = langSymbol === 'he' ? 'hebrew' : langSymbol === 'ar' ? 'arabic' : 'english';
      const titleEl = mapSettingsCtrl.getElement()?.querySelector('.H_rdo_title');
      if (titleEl) titleEl.textContent = CHOOSE_VIEW[langKey];
      this._uiCtrls.push('customMapSettings');
    }

    if (!options.controls.zoom) {
      this._ui.removeControl('zoom');
    } else {
      this._uiCtrls.push('zoom');
    }

    if (!options.controls.scalebar) {
      this._ui.removeControl('scalebar');
    } else {
      this._uiCtrls.push('scalebar');
    }

    const originalMapSetting = this._ui.getControl('mapsettings');
    if (originalMapSetting) {
      this._ui.removeControl('mapsettings');
    }

    this.resizeMapControlButtons();
  }

  private enhanceClusteringOptions(clustering: MarkersClustering): void {
    clustering.layers.forEach((layer) => {
      if (!layer.options) {
        layer.options = {
          eps: 32,
          minWeight: 2,
          clusteringZoomRange: 3,
        } as any;
      }
    });
  }

  private preRenderVisibleTiles(map: any): void {
    try {
      const center = map.getCenter();
      const currentZoom = map.getZoom();

      map.setZoom(currentZoom + 0.01, false);
      map.setZoom(currentZoom, false);

      const directions = [
        { lat: center.lat + 0.01, lng: center.lng },
        { lat: center.lat - 0.01, lng: center.lng },
        { lat: center.lat, lng: center.lng + 0.01 },
        { lat: center.lat, lng: center.lng - 0.01 },
      ];

      directions.forEach((dir) => {
        map.setCenter(dir, false);
      });

      map.setCenter(center, false);
    } catch (e) {
      LogService.logError('Error pre-rendering tiles', e);
    }
  }

  public removeClusteringLayers(): void {
    if (!this._map) return;
    this._layers.forEach(({ layer }) => {
      try {
        this._map!.removeLayer(layer);
      } catch (e) {
        console.warn('[MAP] Failed to remove clustering layer', e);
      }
    });
    this._layers = [];
  }

  public addMarkersClustring(clustering?: MarkersClustering) {
    if (!clustering || !this._map) return;

    clustering.layers.forEach((layer, index) => {
      const dataPoints = layer.dataPoints.map((item) => {
        const coordinates = item.coordinates;
        return new H.clustering.DataPoint(
          coordinates.lat,
          coordinates.lng,
          1,
          item
        );
      });

      const clusteredDataProvider = new H.clustering.Provider(
        !layer.tracking ? dataPoints : [],
        {
          clusteringOptions: layer.options,
        }
      );

      if (layer.dataPointObjectFactory) {
        const theme = clusteredDataProvider.getTheme();
        const originalNoisePresentation =
          theme.getNoisePresentation.bind(theme);
      
        theme.getNoisePresentation = (noisePoint: any) => {
          // Cluster — render as before
          if (noisePoint.isCluster()) {
            return originalNoisePresentation(noisePoint);
          }
      
          // Normalize position
          const pos = noisePoint.getPosition();
          const geo = this.normalizeGeo(pos);
      
          let obj: any = null;
      
          try {
            // Custom factory creates the object
            obj = layer.dataPointObjectFactory!(noisePoint);
      
            // 1) Already a Marker / DomMarker → DON'T call setGeometry!
            // setGeometry can remove event listeners in CDN 3.2
            if (obj instanceof H.map.Marker || obj instanceof H.map.DomMarker) {
              return obj;
            }
      
            // 2) HTMLElement → wrap in DomMarker
            if (obj instanceof HTMLElement) {
              return new H.map.DomMarker(geo, {
                icon: new H.map.DomIcon(obj),
                data: noisePoint.getData(),
              });
            }
      
            // 3) DomIcon → DomMarker
            if (obj instanceof H.map.DomIcon) {
              return new H.map.DomMarker(geo, {
                icon: obj,
                data: noisePoint.getData(),
              });
            }
      
            // Everything else — let HERE render as it knows how
          } catch (e) {
            console.error('[CLUSTER_PATCH] Marker normalization error:', e, obj);
          }
      
          return originalNoisePresentation(noisePoint);
        };
      }
      
    
      const clusteringLayer = new H.map.layer.ObjectLayer(
        clusteredDataProvider
      );

      this._map!.addLayer(clusteringLayer, index + 1);
      this._layers.push({ label: layer.label, layer: clusteringLayer });

      if (layer.tracking?.$location) {
        layer.tracking.$location.subscribe((location) => {
          const newPoints = dataPoints.filter((point: any) => {
            const userPoint = new H.geo.Point(location.lat, location.lng);
            const pointLocation = new H.geo.Point(point.lat, point.lng);
            const distance = userPoint.distance(pointLocation);

            return distance <= layer.tracking!.maxDistance;
          });

          clusteredDataProvider.setDataPoints(newPoints);
        });
      }
    });
  }

  // ------------- GEOMETRY / OBJECTS / VIEW -------------

  private getBounds(locations: Location[]): any {
    let north = -Infinity,
      south = Infinity,
      east = -Infinity,
      west = Infinity;

    locations.forEach((location) => {
      if (location.lat > north) north = location.lat;
      if (location.lat < south) south = location.lat;
      if (location.lng > east) east = location.lng;
      if (location.lng < west) west = location.lng;
    });

    return new H.geo.Rect(north, west, south, east);
  }

  private expandBounds(bounds: any, paddingPercent: number = 0.1): any {
    const latDiff = bounds.getTop() - bounds.getBottom();
    const lngDiff = bounds.getRight() - bounds.getLeft();

    const latPadding = latDiff * paddingPercent;
    const lngPadding = lngDiff * paddingPercent;

    return new H.geo.Rect(
      bounds.getTop() + latPadding,
      bounds.getLeft() - lngPadding,
      bounds.getBottom() - latPadding,
      bounds.getRight() + lngPadding
    );
  }

  private calculateZoomLevel(bounds: any): number {
    const latDiff = bounds.getTop() - bounds.getBottom();
    const lngDiff = bounds.getRight() - bounds.getLeft();

    const worldMapWidthInDegrees = 360;
    const worldMapHeightInDegrees = 180;

    const zoomLat = Math.log2(worldMapHeightInDegrees / latDiff);
    const zoomLng = Math.log2(worldMapWidthInDegrees / lngDiff);

    const calculatedZoom = Math.min(zoomLat, zoomLng);

    // For long routes, set a higher minimum zoom level
    // Determine route length by maximum coordinate difference
    const maxDiff = Math.max(latDiff, lngDiff);
    
    // For very long routes, use fixed zoom for better visibility
    if (maxDiff > 10) {
      return 13;
    } else if (maxDiff > 5) {
      return 8;
    } else if (maxDiff > 3.5) {
      return 8;
    } else if (maxDiff > 2) {
      return 8;
    }
    
    // For other routes, use minimum zoom gradation
    let minZoom = 3;
    if (maxDiff > 1) {
      // Short-medium routes (1-2 degrees) - minimum zoom 9
      // Use 9 instead of 10 to avoid being too close or too far
      minZoom = 9;
    } else if (maxDiff > 0.5) {
      // Short routes (0.5-1 degree) - minimum zoom 7
      minZoom = 9;
    }

    // Use the maximum value between calculated zoom and minimum
    // This ensures routes won't be too far away
    const finalZoom = Math.max(Math.ceil(calculatedZoom), minZoom);
    const result = Math.min(finalZoom, 18);

    return result;
  }

  private trackUserLocation(focusOnUserLocation: boolean) {
    this._trackUserLocationSub?.unsubscribe();

    const onUserLocation = (userLocation: {
      firstLocationCheck?: boolean;
      location: Location;
    }) => {
      const location = userLocation.location;

      if (!!location && !!this._map) {
        if (
          this._myLocationMarker &&
          this._map.getObjects().includes(this._myLocationMarker)
        ) {
          this._myLocationMarker.setGeometry(
            new H.geo.Point(location.lat, location.lng)
          );
        } else {
          this._myLocationMarker = this.addMarker(
            { lat: location.lat, lng: location.lng },
            MAP_ICONS.my_location()
          );
        }

        if (focusOnUserLocation && !this._isPersistentMapInitialized) {
          this.updateMapView(location.lat, location.lng, 15, false);
        }
      }
    };

    const sub = new Subscription();

    sub.add(this.userLocationService.$userLocationChanged.subscribe(onUserLocation));

    sub.add(
      this.userLocationService.$permissionStateChanged
        .pipe(filter((granted) => granted))
        .subscribe(() => {
          this.userLocationService.requestImmediatePositionIfPermitted();
        })
    );

    sub.add(
      this.userLocationService
        .checkPermissionsSilently()
        .pipe(take(1))
        .subscribe((granted) => {
          if (granted) {
            this.userLocationService.requestImmediatePositionIfPermitted();
          }
        })
    );

    this._trackUserLocationSub = sub;
  }

  public addDomMarker(
    location: Location,
    icon?: any,
    data?: any
  ) {
    // Always convert location to H.geo.Point
    const point = new H.geo.Point(location.lat, location.lng);
    const geo = this.normalizeGeo(point);
    // Ensure the icon is valid
    const finalIcon =
      icon instanceof H.map.Icon || icon instanceof H.map.DomIcon
        ? icon
        : undefined;

    const marker = new H.map.DomMarker(geo, {
      icon: finalIcon,
      data,
    });

    this._mapObjects.push(marker);
    this._map!.addObject(marker);

    return marker;
  }

  public addMarker_deprecated(
    location: Location,
    icon?: any,
    data?: any
  ) {
    // Convert to H.geo.Point
    const point = new H.geo.Point(location.lat, location.lng);
    const geo = this.normalizeGeo(point);
    // Icon can be URL or existing Icon instance
    let finalIcon: any;

    if (typeof icon === 'string') {
      finalIcon = new H.map.Icon(icon);
    } else if (icon instanceof H.map.Icon) {
      finalIcon = icon;
    }

    const marker = new H.map.Marker(geo, {
      icon: finalIcon,
      data,
    });

    this._mapObjects.push(marker);
    this._map!.addObject(marker);

    return marker;
  }

  public addMarker(location: Location, icon?: H.map.Icon, data?: any) {
    const marker = new H.map.Marker(location, {
      data: data,
      icon: icon,
    });

    this._mapObjects.push(marker);
    this._map!.addObject(marker);

    return marker;
  }
  
  public addDomMarkerFromIcon(location: Location, icon?: any, data?: any) {
    const geo = new H.geo.Point(location.lat, location.lng);
  
    let finalIcon: H.map.DomIcon;
  
    if (icon instanceof H.map.DomIcon) {
      finalIcon = icon;
    }
  
    else if (icon instanceof H.map.Icon) {
      // Get the image that HERE has already loaded
      const mb = (icon as any).mb || (icon as any).Mb;
      const src = mb?.image?.src || mb?.canvas?.toDataURL?.() || null;
  
      if (!src) {
        console.warn("Icon without src, using fallback");
        finalIcon = new H.map.DomIcon(document.createElement("img"));
      } else {
        const img = document.createElement("img");
        img.src = src;
        img.style.pointerEvents = "auto";
        finalIcon = new H.map.DomIcon(img);
      }
    }
  
    else if (typeof icon === "string") {
      const img = document.createElement("img");
      img.src = icon;
      img.style.pointerEvents = "auto";
      finalIcon = new H.map.DomIcon(img);
    }
  
    else {
      finalIcon = new H.map.DomIcon(document.createElement("div"));
    }
  
    const marker = new H.map.DomMarker(geo, {
      icon: finalIcon,
      data,
    });
  
    this._mapObjects.push(marker);
    this._map!.addObject(marker);
  
    return marker;
  }
  
  
  public drawPolyline(
    lineStrings: any,
    opt?: any
  ) {
    const polyline = new H.map.Polyline(lineStrings, opt);

    this._mapObjects.push(polyline);
    this._map!.addObject(polyline);

    return polyline;
  }

  public drawPolylines(
    lines: {
      lineStrings: any;
      opt?: any;
    }[]
  ) {
    return lines.map((line) => this.drawPolyline(line.lineStrings, line.opt));
  }

  public clearMap() {
    if (!this._map) return;

    try {
      if (this._mapObjects.length) {
        this._map.removeObjects(this._mapObjects);
        this._mapObjects = [];
      }

      const allObjects = this._map.getObjects();
      if (allObjects && allObjects.length > 0) {
        this._map.removeObjects(allObjects);
      }

      this._myLocationMarker = undefined;

      LogService.logDebug('Map cleared, base map maintained');
    } catch (error) {
      LogService.logError('Error clearing map', error);
    }
  }

  public addObject(object: any) {
    if (!this._map) {
      console.warn('Map not initialized, cannot add object');
      return null;
    }
    this._mapObjects.push(object);
    return this._map.addObject(object);
  }

  public removeObject(object: any) {
    if (!!object && this._map!.getObjects().includes(object)) {
      this._map!.removeObject(object);
      this._mapObjects.splice(this._mapObjects.indexOf(object), 1);
    }
  }

  public removeObjects(objects: any[]) {
    if (objects?.length && this._map) {
      this._map.removeObjects(
        objects.filter((object) => this._map!.getObjects().includes(object))
      );

      this._mapObjects = this._mapObjects.filter(
        (mapObject) => !objects.includes(mapObject)
      );
    }
  }

  public updateMapLocation(
    location: Location,
    zoom: number = 15,
    animate: boolean = true
  ) {
    this.updateMapView(location.lat, location.lng, zoom, !!animate);
  }

  public updateMapView(
    lat: number,
    lng: number,
    zoom: number = 15,
    animate: boolean | number = true
  ) {
    if (!!lat) this.lat = lat;
    if (!!lng) this.lng = lng;
    if (!!zoom) this.zoom = zoom;

    LogService.logDebug('Updating map', {
      map: this.map,
      lat: this.lat,
      lng: this.lng,
      zoom: this.zoom,
    });

    if (this._progressiveLoading && this._map) {
      this.applyDetailLevel(this._map);
    }

    if (!this._map) return;

    if (animate) {
      // Get current map state
      const currentLookAt = this._map.getViewModel().getLookAtData();
      const currentLat = currentLookAt.position.lat;
      const currentLng = currentLookAt.position.lng;
      const currentZoom = currentLookAt.zoom;

      // If animate is a number, use it as duration in milliseconds
      // If animate is true, use default duration (300ms for faster animation)
      const duration = typeof animate === 'number' ? animate * 1000 : 300;
      
      // Calculate differences
      const latDiff = this.lat - currentLat;
      const lngDiff = this.lng - currentLng;
      const zoomDiff = this.zoom - currentZoom;

      // If differences are very small, just set directly
      if (Math.abs(latDiff) < 0.0001 && Math.abs(lngDiff) < 0.0001 && Math.abs(zoomDiff) < 0.1) {
        this._map.setZoom(this.zoom, false);
        this._map.setCenter({ lat: this.lat, lng: this.lng }, false);
        return;
      }

      // Use custom animation with requestAnimationFrame for speed control
      const startTime = performance.now();
      const animateStep = (currentTime: number) => {
        const elapsed = currentTime - startTime;
        const progress = Math.min(elapsed / duration, 1);
        
        // Use easing function (ease-out cubic)
        const eased = 1 - Math.pow(1 - progress, 3);
        
        const newLat = currentLat + latDiff * eased;
        const newLng = currentLng + lngDiff * eased;
        const newZoom = currentZoom + zoomDiff * eased;
        
        this._map.setCenter({ lat: newLat, lng: newLng }, false);
        this._map.setZoom(newZoom, false);
        
        if (progress < 1) {
          requestAnimationFrame(animateStep);
        }
      };
      
      requestAnimationFrame(animateStep);
    } else {
      this._map.setZoom(this.zoom, false);
      this._map.setCenter({ lat: this.lat, lng: this.lng }, false);
    }
  }

  public lookAtData(locations: Location[], animate: boolean = true): void {
    if (!this._map) {
      console.warn('Cannot look at data: Map not initialized');
      return;
    }

    if (!locations || locations.length === 0) {
      console.warn('Cannot look at data: No locations provided');
      return;
    }

    const bounds = this.getBounds(locations);

    // Add padding to bounds (10%) so route is not too close to edges
    const expandedBounds = this.expandBounds(bounds, 0.1);

    const calculatedZoom = this.calculateZoomLevel(expandedBounds);

    // Calculate bounds center for position setting
    const centerLat = (expandedBounds.getTop() + expandedBounds.getBottom()) / 2;
    const centerLng = (expandedBounds.getLeft() + expandedBounds.getRight()) / 2;

    // Determine route length to choose strategy
    const latDiff = expandedBounds.getTop() - expandedBounds.getBottom();
    const lngDiff = expandedBounds.getRight() - expandedBounds.getLeft();
    const maxDiff = Math.max(latDiff, lngDiff);

    // For long routes (>2°), use only center + zoom to control zoom
    // For short routes, use bounds to show the entire route
    const useCenterZoomOnly = maxDiff > 2;

    if (useCenterZoomOnly) {
      if (animate) {
        this._map.getViewModel().setLookAtData(
          {
            position: { lat: centerLat, lng: centerLng },
            zoom: calculatedZoom,
          },
          1.5
        );
      } else {
        this._map.setZoom(calculatedZoom, false);
        this._map.setCenter({ lat: centerLat, lng: centerLng }, false);
      }
    } else {
      if (animate) {
        // Set bounds with animation
        this._map.getViewModel().setLookAtData(
          {
            bounds: expandedBounds,
          },
          1.5
        );
        
        // Then set the required zoom separately with a longer delay
        setTimeout(() => {
          this._map.getViewModel().setLookAtData(
            {
              position: { lat: centerLat, lng: centerLng },
              zoom: calculatedZoom,
            },
            0.5
          );
        }, 200);
      } else {
        // Without animation - set everything at once
        this._map.getViewModel().setLookAtData(
          {
            bounds: expandedBounds,
          },
          false
        );
        // Set zoom separately
        this._map.setZoom(calculatedZoom, false);
        this._map.setCenter({ lat: centerLat, lng: centerLng }, false);
      }
    }

  }

  public moveNorth(location: Location, distance: number): Location {
    const deltaLat = distance / 111111;
    return { ...location, lat: location.lat + deltaLat };
  }

  public moveSouth(location: Location, distance: number): Location {
    const deltaLat = distance / 111111;
    return { ...location, lat: location.lat - deltaLat };
  }

  public moveEast(location: Location, distance: number): Location {
    const deltaLng =
      distance / (111111 * Math.cos((location.lat * Math.PI) / 180));
    return { ...location, lng: location.lng + deltaLng };
  }

  public moveWest(location: Location, distance: number): Location {
    const deltaLng =
      distance / (111111 * Math.cos((location.lat * Math.PI) / 180));
    return { ...location, lng: location.lng - deltaLng };
  }

  public setUiLayout(alignment: any) {
    if (!!this._ui) {
      this._uiCtrls.forEach((ctrl) => {
        const c = this._ui!.getControl(ctrl);
        if (c) c.setAlignment(alignment);
      });
    }
  }

  public removeMarkerObjects(): void {
    const markerObjects = this._mapObjects.filter(
      (obj) => obj instanceof H.map.Marker || obj instanceof H.map.DomMarker
    );
    this.removeObjects(markerObjects);
  }

  public reset(): void {
    this._trackUserLocationSub?.unsubscribe();
    this._trackUserLocationSub = undefined;

    if (this._map) {
      this._map.dispose();
      LogService.logInfo('Map instance disposed via reset()');
      this._map = undefined;
      this._isPersistentMapInitialized = false;

      this.$mapInitialized = new ReplaySubject<any>();

      const now = new Date();
      const dd = String(now.getDate()).padStart(2, '0');
      const MM = String(now.getMonth() + 1).padStart(2, '0');
      const yyyy = now.getFullYear();
      const hh = String(now.getHours()).padStart(2, '0');

      const formatted = `${dd}${MM}${yyyy}T${hh}:00:00`;
      this._instanceId = 'map_' + formatted;
    }
  }

  public captureMap(
    zoomAdjustment: number = 0,
    options: {
      width?: number;
      height?: number;
      quality?: number;
    } = {}
  ): Promise<string> {
    return new Promise((resolve, reject) => {
      if (!this._map) {
        reject('Map not initialized');
        return;
      }

      try {
        const currentZoom = this._map.getZoom();

        if (zoomAdjustment !== 0) {
          this._map.setZoom(currentZoom, false);
        }

        requestAnimationFrame(() => {
          try {
            const mapContainer = this._map.getElement();

            html2canvas(mapContainer, {
              useCORS: true,
              allowTaint: true,
              backgroundColor: null,
              scale: 1.0, // Scale 1.0 provides best balance for performance
              logging: false,
              foreignObjectRendering: false,
              removeContainer: false,
              proxy: undefined, // Disable proxy for better performance
              ignoreElements: (element) => {
                // Skip hidden elements to speed up rendering
                const style = window.getComputedStyle(element);
                if (style.display === 'none' || style.visibility === 'hidden' || style.opacity === '0') {
                  return true;
                }
                // Skip script and style tags
                const tagName = element.tagName?.toLowerCase();
                if (['script', 'style', 'noscript', 'meta', 'link'].includes(tagName)) {
                  return true;
                }
                // Skip elements with zero dimensions
                if (element instanceof HTMLElement) {
                  const rect = element.getBoundingClientRect();
                  if (rect.width === 0 && rect.height === 0) {
                    return true;
                  }
                }
                return false;
              },
            })
              .then((canvas) => {
                if (zoomAdjustment !== 0) {
                  this._map.setZoom(currentZoom, false);
                }

                try {
                  if (options.width && options.height) {
                    let offscreenCanvas: any;
                    let ctx:
                      | CanvasRenderingContext2D
                      | OffscreenCanvasRenderingContext2D;

                    if (typeof OffscreenCanvas !== 'undefined') {
                      offscreenCanvas = new OffscreenCanvas(
                        options.width,
                        options.height
                      );
                      ctx = offscreenCanvas.getContext('2d', { alpha: true })!;
                    } else {
                      offscreenCanvas = document.createElement('canvas');
                      offscreenCanvas.width = options.width;
                      offscreenCanvas.height = options.height;
                      ctx = offscreenCanvas.getContext('2d', {
                        willReadFrequently: true,
                        alpha: true,
                      }) as CanvasRenderingContext2D;
                    }

                    ctx.drawImage(canvas, 0, 0, options.width, options.height);

                    if (offscreenCanvas instanceof OffscreenCanvas) {
                      offscreenCanvas
                        .convertToBlob({
                          type: 'image/png',
                          quality: options.quality || 0.9,
                        })
                        .then((blob) => {
                          const reader = new FileReader();
                          reader.onloadend = () =>
                            resolve(reader.result as string);
                          reader.readAsDataURL(blob);
                        });
                    } else {
                      resolve(
                        offscreenCanvas.toDataURL(
                          'image/png',
                          options.quality || 0.9
                        )
                      );
                    }
                  } else {
                    resolve(
                      canvas.toDataURL('image/png', options.quality || 0.9)
                    );
                  }
                } catch (canvasErr) {
                  console.error('Error processing canvas:', canvasErr);

                  this._map.capture((capturedCanvas: HTMLCanvasElement) => {
                    if (capturedCanvas) {
                      const backupCanvas = document.createElement('canvas');
                      backupCanvas.width =
                        options.width || capturedCanvas.width;
                      backupCanvas.height =
                        options.height || capturedCanvas.height;

                      const backupCtx = backupCanvas.getContext('2d', {
                        willReadFrequently: true,
                      })!;

                      backupCtx.drawImage(
                        capturedCanvas,
                        0,
                        0,
                        backupCanvas.width,
                        backupCanvas.height
                      );

                      resolve(
                        backupCanvas.toDataURL(
                          'image/png',
                          options.quality || 0.9
                        )
                      );
                    } else {
                      reject('Failed to capture map canvas in fallback mode');
                    }
                  });
                }
              })
              .catch((err) => {
                console.error('Html2Canvas error:', err);

                this._map.capture((capturedCanvas: HTMLCanvasElement) => {
                  if (zoomAdjustment !== 0) {
                    this._map.setZoom(0, false);
                  }

                  if (capturedCanvas) {
                    try {
                      const backupCanvas = document.createElement('canvas');
                      backupCanvas.width =
                        options.width || capturedCanvas.width;
                      backupCanvas.height =
                        options.height || capturedCanvas.height;

                      const backupCtx = backupCanvas.getContext('2d', {
                        willReadFrequently: true,
                      })!;

                      backupCtx.drawImage(
                        capturedCanvas,
                        0,
                        0,
                        backupCanvas.width,
                        backupCanvas.height
                      );

                      resolve(
                        backupCanvas.toDataURL(
                          'image/png',
                          options.quality || 0.9
                        )
                      );
                    } catch (fallbackErr) {
                      console.error(
                        'Fallback canvas processing error:',
                        fallbackErr
                      );
                      reject(fallbackErr);
                    }
                  } else {
                    reject('Failed to capture map canvas in fallback mode');
                  }
                });
              });
          } catch (renderError) {
            console.error('Map rendering error:', renderError);
            reject(renderError);
          }
        });
      } catch (error) {
        console.error('Error in captureMap:', error);
        reject(error);
      }
    });
  }

  public reconnectMapToElement(mapDiv: ElementRef): boolean {
    try {
      if (!this._map || !this._isPersistentMapInitialized) {
        return false;
      }

      const currentCenter = this._map.getCenter();
      const currentZoom = this._map.getZoom();

      this.clearMap();

      const currentBaseLayer = this._map.getBaseLayer();

      const oldMap = this._map;

      this.ngZone.runOutsideAngular(() => {
        const map = new H.Map(mapDiv.nativeElement, currentBaseLayer, {
          pixelRatio: window.devicePixelRatio,
          center: currentCenter,
          zoom: currentZoom,
          engineType: this.engineType,
        });

        if (this._mapBehavior) this._mapBehavior.dispose();
        if (this._mapEvents) this._mapEvents.dispose();

        this._mapEvents = new H.mapevents.MapEvents(map);
        this._mapBehavior = new H.mapevents.Behavior(this._mapEvents);

        if (this._ui) {
          this._ui.dispose();
          this._ui = H.ui.UI.createDefault(map, this._defaultLayers);
          this.resizeMapControlButtons();

          this._uiCtrls.forEach((ctrl) => {
            if (
              ctrl === 'myLocation' &&
              this.userLocationService.hasPermissions
            ) {
              this._ui.addControl(
                'myLocation',
                new MyLocationControl(this as any, this.userLocationService)
              );
            }
          });
        }

        (map as any).__instanceId = (oldMap as any).__instanceId;

        this._map = map;
        setTimeout(() => {
          oldMap.dispose();
        }, 100);

        onResize(mapDiv.nativeElement, () => {
          map.getViewPort().resize();
        });

        if (this._progressiveLoading) {
          this.applyDetailLevel(map);

          map.addEventListener('mapviewchangeend', () => {
            this.applyDetailLevel(map);

            const location = map.getViewModel().getLookAtData().position;
            this.ngZone.run(() => {
              this.$mapViewChanged.next({
                lat: location.lat,
                lng: location.lng,
              });
            });
          });
        } else {
          map.addEventListener('mapviewchangeend', () => {
            const location = map.getViewModel().getLookAtData().position;
            this.ngZone.run(() => {
              this.$mapViewChanged.next({
                lat: location.lat,
                lng: location.lng,
              });
            });
          });
        }
      });

      this.$isPersistentMapReady.next(true);
      return true;
    } catch (ex) {
      console.error(
        `[MAP_TRACKING] Failed to reconnect map to new element`,
        ex
      );
      LogService.logError('Failed to reconnect map to new element', ex);
      return false;
    }
  }

  public get engineType() {
    return H.Map.EngineType.HARP;
  }

  public normalizeGeo(pos: any): H.geo.IPoint {
    if (!pos) throw new Error("Empty position");
  
    // If it's already a HERE Point
    if (pos instanceof H.geo.Point) return pos;
  
    // If shape is {lat, lng}
    if ("lat" in pos && "lng" in pos)
      return new H.geo.Point(pos.lat, pos.lng);
  
    // If shape is {latitude, longitude}
    if ("latitude" in pos && "longitude" in pos)
      return new H.geo.Point(pos.latitude, pos.longitude);
  
    // If shape is {coordinates:{lat,lng}}
    if ("coordinates" in pos)
      return new H.geo.Point(pos.coordinates.lat, pos.coordinates.lng);
  
    console.error("❌ Unknown geo format:", pos);
    throw new Error("Invalid geo input");
  }

  public wrapToDomIcon(icon: any): H.map.DomIcon {
    // 1. If already DomIcon → return as is
    if (icon instanceof H.map.DomIcon) {
      return icon;
    }
  
    // 2. If not an Icon at all → fallback
    if (!(icon instanceof H.map.Icon)) {
      console.warn("[wrapToDomIcon] Not an Icon → fallback used", icon);
  
      const wrapper = document.createElement("div");
      wrapper.style.width = "32px";
      wrapper.style.height = "32px";
      wrapper.style.background = "red";     // To see the error marker
      wrapper.style.pointerEvents = "auto";
  
      return new H.map.DomIcon(wrapper);
    }
  
    // 3. Extract bitmap safely
    const bmp = icon.getBitmap() as unknown;
  
    let src: string | null = null;
  
    if (typeof bmp === "string") {
      src = bmp;
    } else if (bmp instanceof HTMLCanvasElement) {
      src = bmp.toDataURL();
    }
  
    if (!src) {
      console.warn("[wrapToDomIcon] Icon without bitmap → fallback");
      src = "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAAB...";
    }
  
    // 4. IMG element
    const img = document.createElement("img");
    img.src = src;
    img.style.width = "inherit";
    img.style.height = "inherit";
    img.style.pointerEvents = "auto";
  
    // 5. Size
    const size = icon.getSize() ?? { w: 32, h: 32 };
  
    const wrapper = document.createElement("div");
    wrapper.style.width = `${size.w}px`;
    wrapper.style.height = `${size.h}px`;
    wrapper.style.pointerEvents = "auto";
  
    wrapper.appendChild(img);
  
    return new H.map.DomIcon(wrapper);
  }
  
  

  private enableHarpEvents(map: H.Map) {
    map.addEventListener('tap', (ev: any) => {
      const target = ev.target;
  
      // 1) Click on Marker or DomMarker
      if (target instanceof H.map.Marker || target instanceof H.map.DomMarker) {
        // No dispatchEvent(target) — THIS CAUSES RECURSION
        // We just call the custom handler directly
        if (typeof target['__onTap'] === 'function') {
          target['__onTap'](ev);
        }
        return;
      }
  
      // 2) Click on map → forward as "maptap"
      if (target instanceof H.Map) {
        const maptap = new H.util.Event('maptap', map);
        //@ts-ignore
        maptap.currentPointer = ev.currentPointer;
        map.dispatchEvent(maptap);
      }
    });
  }
  
  private hideDisputedBorders() {
    try {
      const provider = this._map.getBaseLayer().getProvider();
      const style = provider.getStyle();
  
      if (!style || !style.layers) {
        console.warn("STYLE NOT READY");
        return;
      }

      Object.values(style.layers).forEach((layer: any) => {
        if (!layer) return;
  
        if (
          layer.id?.toLowerCase().includes("boundary") &&
          layer.style?.line &&
          (
            layer.style.line.dash ||
            layer.id.toLowerCase().includes("disputed")
          )
        ) {
          layer.style.line.color = "rgba(0,0,0,0)";
          layer.style.line.width = 0;
          layer.style.line.dash = [];     // Dashed → solid → 0
        }
      });
  
      provider.setStyle(style);  // <-- important!
    } catch(err) {
      console.error("hideDisputedBordersReal ERROR", err);
    }
  }
  
  
  public drawCustomBorderLineString(feature: any): void {
    if (!this._map) return;
    if (!feature.geometry || feature.geometry.type !== "LineString") return;
  
    const lineString = new H.geo.LineString();
  
    feature.geometry.coordinates.forEach(([lng, lat]: any) => {
      lineString.pushLatLngAlt(lat, lng, 0);
    });
  
    const polyline = new H.map.Polyline(lineString, {
      style: {
        strokeColor: "#d783a5",
        lineWidth: 1.0,
        //@ts-ignore
        lineJoin: "round",
        lineCap: "round",
      }
    });
  
    
    this.addObject(polyline);
  }

  public drawBordersFromGeoJSON(collection: any): void {
    if (!collection || !Array.isArray(collection.features)) return;
  
    collection.features.forEach((f: any) => {
      if (f.geometry?.type === "LineString") {
        this.drawCustomBorderLineString(f);
      }
    });
  }

  private patchHereStyle() {
    const originalFetch = window.fetch;
    // Patch only once
    if ((window as any).__hereStylePatched) return;
    (window as any).__hereStylePatched = true;
  
    window.fetch = async (url, init) => {
      if (typeof url === "string" && url.includes("normal.day.json")) {
        const resp = await originalFetch(url, init);
        const json = await resp.json();
    
        const defs = json.definitions || {};
    
        // 🔥 EXACT FIELDS THAT NEED TO BE KILLED
        const kill = [
          "CountryBoundary.Disputed.Color",
          "CountryBoundary.Disputed.Outline.Color",
          "CountryBoundary.Disputed.Width",
          "CountryBoundary.Disputed.Outline.Width"
        ];
    
        kill.forEach(key => {
          if (defs[key]) {
            // Make color fully transparent
            if (Array.isArray(defs[key]) && defs[key][0] === "interpolate") {
              // Replace with constant
              defs[key] = "#00000000";
            }
            // If it's a ref or number — set to zero
            else {
              defs[key] = 0;
            }
          }
        });
    
        return new Response(JSON.stringify(json), {
          headers: { "Content-Type": "application/json" }
        });
      }
    
      return originalFetch(url, init);
    };
  }

  private resizeMapControlButtons(): void {
    const size = this.resizeService.isMobile ? '35px' : '45px';
    const controlNames = ['zoom', 'customMapSettings', 'myLocation'];
    controlNames.forEach((name) => {
      const control = this._ui?.getControl(name);
      if (!control) return;
      const el = control.getElement();
      if (!el) return;
      const buttons = el.querySelectorAll(':scope > .H_btn') as NodeListOf<HTMLElement>;
      buttons.forEach((btn) => {
        btn.style.width = size;
        btn.style.height = size;
      });
    });
  }
}
